import MessageHandlebar from '@/model/MessageHandlebar';
import i18n from '@/plugins/i18n';

const className = 'handlebar';

const paragraphHtmlRe = /^<p(\s[^>]*)?>[\s\S]*<\/p>$/;

function isParagraphHtml(line: string): boolean {
  return paragraphHtmlRe.test(line);
}

/** Convert TipTap paragraph HTML to stored text. Bare <p> becomes a newline; attributed
 *  <p> (e.g. style="text-align: center") is kept so alignment survives the round-trip. */
function htmlParagraphsToText(html: string): string {
  const matches = Array.from(html.matchAll(/<p(\s[^>]*)?>([\s\S]*?)<\/p>/g));
  if (matches.length === 0) return html;

  const parts: string[] = [];
  let lastIndex = 0;
  matches.forEach((match, i) => {
    const [full, attrs, inner] = match;
    const index = match.index ?? 0;
    const before = html.slice(lastIndex, index);
    if (before) {
      parts.push(before);
    } else if (i > 0) {
      parts.push('\n');
    }
    if (attrs && attrs.trim()) {
      parts.push(`<p${attrs}>${inner}</p>`);
    } else {
      parts.push(inner);
    }
    lastIndex = index + full.length;
  });
  if (lastIndex < html.length) {
    parts.push(html.slice(lastIndex));
  }
  return parts.join('');
}

export function textWithHandlebarsToHtml(text: String | null, handlebars:MessageHandlebar[]): string {
  if (!text) return '<p></p>';

  let newtext = text;

  handlebars.forEach((handlebar) => {
    const name = i18n.t(`code.handlebar.${handlebar.name}`);
    const r = `<span data-handlebar-id="${handlebar.id}" label="${name}" class="${className}">${name}</span>`;
    newtext = newtext.replace(new RegExp(handlebar.expression, 'g'), r);
  });

  return newtext
    .split(/\r\n|\r|\n/)
    .map((line) => (isParagraphHtml(line) ? line : `<p>${line}</p>`))
    .join('');
}

export function htmlToTextWithHandlebars(
  text: string | null,
  handlebarsById: Map<number, MessageHandlebar>,
): string | null {
  if (!text) return null;
  let newtext = htmlParagraphsToText(text);

  const regexp = `<span data-handlebar-id="([\\d]+)" label="[^"]*" class="${className}">([^<]*)</span>`;

  // eslint-disable-next-line no-constant-condition
  while (1) {
    const result = newtext.match(regexp);
    if (!result) break;
    const [match, id, name] = [...result];
    const replacement = handlebarsById.get(Number(id))?.expression ?? name ?? '';
    // plain string replacement: the matched HTML may contain regex metachars (e.g. the
    // label "Start Date (short)") - new RegExp(match) then matches nothing and this
    // loop spins forever
    const replaced = newtext.split(match).join(replacement);
    if (replaced === newtext) break;
    newtext = replaced;
  }
  return newtext;
}
